import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';

import {getSessionTokenCookieName} from 'frontend/utils/session_cookie';

/**
 * Extracts a cookie value from document.cookie by cookie name.
 */
export function getCookieValueByName(cookieName: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${encodeURIComponent(cookieName)}=([^;]*)`),
  );
  if (!match || match.length < 2 || match[1] === '') {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * Extracts the SSO session token from document.cookie, using the cookie name
 * that belongs to the environment currently being served.
 */
export function getSsoSessionToken(): string | null {
  return getCookieValueByName(getSessionTokenCookieName());
}

/**
 * Retrieves HTTP authorization headers based on the current SSO session token.
 * Returns a record with Authorization Bearer header if a valid SSO token exists,
 * or undefined otherwise.
 */
export function getAuthHeaders(): Record<string, string> | undefined {
  const sessionToken = getSsoSessionToken();
  if (sessionToken !== null && sessionToken !== '') {
    return {
      Authorization: `Bearer ${sessionToken}`,
    };
  }
  return undefined;
}

/**
 * Angular HTTP Interceptor that extracts the SSO session token from cookies
 * and attaches it to outgoing HTTP requests.
 *
 * The backend services require authentication
 * via the `Authorization: Bearer <token>` header. Since the frontend receives
 * the session token via a cookie (shared across the domain by the SSO app),
 * this interceptor automatically propagates that token to all API calls.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const authHeaders = getAuthHeaders();
    if (authHeaders) {
      const authenticatedRequest = request.clone({
        setHeaders: authHeaders,
      });
      return next.handle(authenticatedRequest);
    }
    return next.handle(request);
  }
}
